import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, FileText, Send, Search, Loader2, CheckCircle2,
  AlertTriangle, RefreshCw, XCircle, Package, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';
import { exportArrayToCsv } from '@/lib/csvExport';

const statusMeta = (status) => {
  switch (status) {
    case 'completed': return { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
    case 'completed_with_errors': return { label: 'Completed with errors', cls: 'bg-amber-100 text-amber-700', icon: AlertTriangle };
    case 'running': return { label: 'Running', cls: 'bg-blue-100 text-blue-700', icon: Loader2 };
    case 'queued': return { label: 'Queued', cls: 'bg-slate-100 text-slate-600', icon: Loader2 };
    case 'failed': return { label: 'Failed', cls: 'bg-red-100 text-red-700', icon: XCircle };
    default: return { label: status || '—', cls: 'bg-gray-100 text-gray-600', icon: RefreshCw };
  }
};

const r3 = (v) => Math.round(v * 1000) / 1000;

const rowMetrics = (r) => {
  const isRM = !!r.is_running_mtr;
  const markerVal = (isRM ? parseFloat(r.qty) : parseInt(r.qty)) || 0;
  const sortVal = parseFloat(r.sort_qty) || 0;
  const bundleWt = (r.marker_weight != null && r.marker_weight !== '') ? Number(r.marker_weight) : null;
  const perPc = (bundleWt != null && markerVal) ? bundleWt / markerVal : null;
  const sortWt = (r.sort_weight != null && r.sort_weight !== '') ? Number(r.sort_weight) : null;
  const wtDiff = (perPc != null && sortWt != null) ? (perPc - sortWt) : null;
  return { isRM, markerVal, sortVal, diff: r3(markerVal - sortVal), bundleWt, perPc, sortWt, wtDiff };
};

const rowStatusText = (r) => [
  r.split_from && `Split from ${r.split_from}`,
  r.is_running_mtr && 'Running Mtr',
  r.reconciled && 'Reconciled',
  !r.reconciled && r.packed && 'Packed',
  !r.packed && 'Not packed',
].filter(Boolean).join(' | ');

const fix = (v, d) => (v != null ? v.toFixed(d) : '');

const exportReconcileCsv = (docData, rows) => {
  const data = rows.map((r, idx) => {
    const m = rowMetrics(r);
    return {
      sno: idx + 1, document: docData.document_barcode, vendor: docData.vendor_name || '',
      bundle: r.bundle_barcode, sort_no: r.sort_no || '', colour: r.color_name || '', status: rowStatusText(r),
      sort_qty: r.sort_qty ?? '', marker_qty: r.qty ?? '', diff: m.diff,
      bundle_wt: fix(m.bundleWt, 2), per_pc: fix(m.perPc, 3), sort_wt: fix(m.sortWt, 3), wt_diff: fix(m.wtDiff, 3),
    };
  });
  const columns = [
    { key: 'sno', header: '#' }, { key: 'document', header: 'Document' }, { key: 'vendor', header: 'Vendor' },
    { key: 'bundle', header: 'Bundle' }, { key: 'sort_no', header: 'Sort No' }, { key: 'colour', header: 'Colour' },
    { key: 'status', header: 'Status' },
    { key: 'sort_qty', header: 'Sort No Qty' }, { key: 'marker_qty', header: 'Marker Qty' }, { key: 'diff', header: 'Difference' },
    { key: 'bundle_wt', header: 'Bundle Wt (kg)' }, { key: 'per_pc', header: 'Per-Pc Wt (kg)' },
    { key: 'sort_wt', header: 'Sort Wt (kg)' }, { key: 'wt_diff', header: 'Wt Diff (Pc-Sort)' },
  ];
  exportArrayToCsv(data, columns, `reconcile_${docData.document_barcode.replace(/[^A-Za-z0-9-]+/g, '_')}.csv`);
};

const Reconcile = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);

  const [docBarcode, setDocBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [docData, setDocData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rows, setRows] = useState([]); // [{bundle_barcode, qty, ...}]

  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [report, setReport] = useState([]);

  const staffInputRef = useRef(null);
  const docInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { staffInputRef.current?.focus(); loadReport(); }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
      setTimeout(() => docInputRef.current?.focus(), 100);
    }
  };

  const loadReport = async () => {
    try {
      const res = await apiClient.get('/sections/greige/reconcile/report');
      setReport(res.data.jobs || []);
    } catch (e) { /* non-blocking */ }
  };

  const handleFetch = async (e) => {
    e?.preventDefault();
    const doc = docBarcode.trim();
    if (!doc) { toast.error('Enter a document number'); return; }
    setLoading(true); setDocData(null); setNotFound(false); setRows([]); setJob(null);
    try {
      const res = await apiClient.get('/sections/greige/reconcile/fetch', { params: { document_barcode: doc } });
      if (!res.data.found || (res.data.total_bundles ?? 0) === 0) {
        setNotFound(true);
      } else {
        setDocData(res.data);
        setRows(res.data.bundles.map(b => ({ ...b, qty: b.marker_qty || 0 })));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch document');
    } finally {
      setLoading(false);
    }
  };

  const updateQty = (barcode, value) => {
    setRows(prev => prev.map(r => r.bundle_barcode === barcode ? { ...r, qty: value } : r));
  };

  const updateSortQty = (barcode, value) => {
    setRows(prev => prev.map(r => r.bundle_barcode === barcode ? { ...r, sort_qty: value } : r));
  };

  const enteredTotal = r3(rows.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0));
  const sortTotal = r3(rows.reduce((s, r) => s + (parseFloat(r.sort_qty) || 0), 0));
  const weightTotal = rows.reduce((s, r) => s + (parseFloat(r.marker_weight) || 0), 0);
  const sortMarkerDiff = r3(enteredTotal - sortTotal);
  const docTotal = docData?.document_total_qty ?? 0;
  const discrepancy = r3(enteredTotal - docTotal);
  const reconciledCount = rows.filter(r => r.reconciled).length;
  const pendingCount = rows.length - reconciledCount;

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let consecutiveErrors = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/sections/greige/reconcile/job/${jobId}`);
        consecutiveErrors = 0;
        setJob(res.data);
        if (['completed', 'completed_with_errors', 'failed'].includes(res.data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadReport();
          if (res.data.status === 'completed') toast.success(`Reconciliation complete — ${res.data.reconciled_qty || 0} pcs reconciled`);
          else if (res.data.status === 'completed_with_errors') toast.warning('Reconciliation finished with some errors — check details');
          else toast.error('Reconciliation failed — check details');
        }
      } catch (e) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= 5) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setJob(prev => prev ? { ...prev, status: 'failed', error: 'Lost connection to the reconciliation job — please refresh the Reconcile Report to check its final status.' } : prev);
          toast.error('Lost connection to the reconciliation job — check the Reconcile Report for status.');
        }
      }
    }, 1500);
  };

  const handleSubmit = async () => {
    const bundles = rows.filter(r => !r.reconciled && (parseFloat(r.qty) || 0) > 0)
      .map(r => ({
        bundle_barcode: r.bundle_barcode,
        qty: r.is_running_mtr ? parseFloat(r.qty) : parseInt(r.qty),
        sort_qty: (r.sort_qty === '' || r.sort_qty == null) ? null : parseFloat(r.sort_qty),
      }));
    if (bundles.length === 0) {
      toast.error('No un-reconciled bundles to submit — these bundle(s) are already reconciled');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post('/sections/greige/reconcile/submit', {
        staff_id: staffId,
        document_barcode: docData.document_barcode,
        bundles,
      });
      setJob({ job_id: res.data.job_id, status: res.data.status, total_bundles: res.data.total_bundles, total_qty: res.data.total_qty, processed_bundles: 0, reconciled_qty: 0, errors: [] });
      pollJob(res.data.job_id);
      toast.success('Reconciliation started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start reconciliation');
    } finally {
      setSubmitting(false);
    }
  };

  const jobRunning = job && ['queued', 'running'].includes(job.status);
  const progressPct = job && job.total_bundles ? Math.round((job.processed_bundles / job.total_bundles) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Reconcile Operation</h1>
              <p className="text-sm text-[#E8DCC8]">Confirm document qty per bundle (before Internal Transfer)</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Staff */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg"><User className="w-5 h-5 text-violet-600" /> Supervisor Identification</CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput inputRef={staffInputRef} onValidated={handleStaffValidated} autoFocus={true} label="" />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-violet-600 text-white px-4 py-2 rounded-lg font-mono text-lg">{staffId}</div>
                  {staffName && <span className="text-[#5A5F6E]">{staffName}</span>}
                </div>
                <Button variant="outline" onClick={() => { setStaffIdLocked(false); setStaffId(''); setStaffName(''); }}>Change</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Document fetch */}
        {staffIdLocked && (
          <Card className="border-2 border-violet-600">
            <CardHeader className="pb-3 bg-violet-50">
              <CardTitle className="flex items-center gap-2 text-lg"><FileText className="w-5 h-5 text-violet-600" /> Document Number</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleFetch} className="flex gap-3">
                <Input ref={docInputRef} value={docBarcode} onChange={(e) => setDocBarcode(e.target.value)} placeholder="Enter / scan document number (e.g. DOC000756/26-27)" className="text-lg h-12 font-mono" autoComplete="off" data-testid="reconcile-doc-input" />
                <Button type="submit" className="h-12 px-6 bg-violet-600 hover:bg-violet-700" disabled={loading} data-testid="reconcile-fetch-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                  <span className="ml-2 hidden sm:inline">Fetch</span>
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {notFound && (
          <Card><CardContent className="py-10 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No bundles found for “{docBarcode.trim()}”</p>
          </CardContent></Card>
        )}

        {/* Reconcile table */}
        {docData && rows.length > 0 && (
          <Card className="border-2 border-[#E5DFD0]" data-testid="reconcile-result">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">{docData.document_barcode}</CardTitle>
                  <CardDescription>{docData.vendor_name || '—'} • {docData.total_bundles} bundles</CardDescription>
                </div>
                <div className="flex items-center gap-3 text-sm flex-wrap">
                  <div className="text-center px-3 py-1.5 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500">Document Total</p>
                    <p className="font-bold text-slate-800" data-testid="doc-total">{docTotal}</p>
                  </div>
                  <div className="text-center px-3 py-1.5 bg-amber-100 rounded-lg">
                    <p className="text-xs text-amber-600">Sort No Total</p>
                    <p className="font-bold text-amber-800" data-testid="sort-total">{sortTotal}</p>
                  </div>
                  <div className="text-center px-3 py-1.5 bg-violet-100 rounded-lg">
                    <p className="text-xs text-violet-500">Marker Total</p>
                    <p className="font-bold text-violet-800" data-testid="entered-total">{enteredTotal}</p>
                  </div>
                  <div className={`text-center px-3 py-1.5 rounded-lg ${sortMarkerDiff === 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <p className={`text-xs ${sortMarkerDiff === 0 ? 'text-emerald-600' : 'text-red-500'}`}>Marker − Sort</p>
                    <p className={`font-bold ${sortMarkerDiff === 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="sort-marker-diff">
                      {sortMarkerDiff > 0 ? '+' : ''}{sortMarkerDiff}
                    </p>
                  </div>
                  <div className={`text-center px-3 py-1.5 rounded-lg ${discrepancy === 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <p className={`text-xs ${discrepancy === 0 ? 'text-emerald-600' : 'text-red-500'}`}>vs Document</p>
                    <p className={`font-bold ${discrepancy === 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="discrepancy">
                      {discrepancy > 0 ? '+' : ''}{discrepancy}
                    </p>
                  </div>
                  <div className="text-center px-3 py-1.5 bg-sky-100 rounded-lg">
                    <p className="text-xs text-sky-600">Total Wt (kg)</p>
                    <p className="font-bold text-sky-800" data-testid="weight-total">{weightTotal.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[420px] overflow-auto border rounded-lg">
                <Table data-testid="reconcile-table">
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Bundle</TableHead>
                      <TableHead>Sort No</TableHead>
                      <TableHead>Colour</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Sort No Qty</TableHead>
                      <TableHead className="text-right">Marker Qty</TableHead>
                      <TableHead className="text-right">Difference</TableHead>
                      <TableHead className="text-right">Bundle Wt (kg)</TableHead>
                      <TableHead className="text-right">Per-Pc Wt (kg)</TableHead>
                      <TableHead className="text-right">Sort Wt (kg)</TableHead>
                      <TableHead className="text-right">Wt Δ (Pc−Sort)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, idx) => {
                      const { isRM, diff, bundleWt, perPc, sortWt, wtDiff } = rowMetrics(r);
                      return (
                      <TableRow key={r.bundle_barcode} data-testid={`reconcile-row-${idx}`}>
                        <TableCell className="text-gray-400 text-sm">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm font-medium">{r.bundle_barcode}</TableCell>
                        <TableCell className="text-sm">{r.sort_no || '—'}</TableCell>
                        <TableCell className="text-sm" data-testid={`reconcile-colour-${idx}`}>
                          {r.color_name ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="w-4 h-4 rounded border border-gray-300 shrink-0" style={{ backgroundColor: r.color_rgb || '#e5e7eb' }} data-testid={`reconcile-colour-swatch-${idx}`} />
                              <span className="whitespace-nowrap">{r.color_name}</span>
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {r.split_from && <Badge className="bg-purple-100 text-purple-700 text-[10px] whitespace-nowrap" title={`This bundle was split from the scrapped receipt code ${r.split_from}`} data-testid={`reconcile-split-${idx}`}>Split from {r.split_from}</Badge>}
                            {isRM && <Badge className="bg-orange-100 text-orange-700 text-[10px]" data-testid={`reconcile-runningmtr-${idx}`}>Running Mtr · 1 pc</Badge>}
                            {r.reconciled && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Reconciled</Badge>}
                            {!r.reconciled && r.packed && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Packed</Badge>}
                            {!r.packed && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Not packed</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right" data-testid={`reconcile-sortqty-${idx}`}>
                          <Input
                            type="number" min="0" step="any"
                            value={r.sort_qty ?? ''}
                            onChange={(e) => updateSortQty(r.bundle_barcode, e.target.value)}
                            className="w-24 h-9 text-right font-semibold text-amber-700 inline-block"
                            disabled={r.reconciled}
                            data-testid={`reconcile-sortqty-input-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number" min="0" step={isRM ? '0.01' : '1'}
                              value={r.qty}
                              onChange={(e) => updateQty(r.bundle_barcode, e.target.value)}
                              className="w-24 h-9 text-right font-semibold inline-block"
                              disabled={r.reconciled}
                              data-testid={`reconcile-qty-${idx}`}
                            />
                            <span className="text-xs text-[#5A5F6E] w-6">{isRM ? 'm' : ''}</span>
                          </div>
                        </TableCell>
                        <TableCell className={`text-right font-bold ${diff === 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`reconcile-diff-${idx}`}>
                          {diff > 0 ? '+' : ''}{diff}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium text-sky-700" data-testid={`reconcile-bundlewt-${idx}`}>{bundleWt != null ? bundleWt.toFixed(2) : '—'}</TableCell>
                        <TableCell className="text-right text-sm" data-testid={`reconcile-perpc-${idx}`}>{perPc != null ? perPc.toFixed(3) : '—'}</TableCell>
                        <TableCell className="text-right text-sm text-amber-700" data-testid={`reconcile-sortwt-${idx}`}>{sortWt != null ? sortWt.toFixed(3) : '—'}</TableCell>
                        <TableCell className={`text-right text-sm font-semibold ${wtDiff == null ? 'text-gray-400' : Math.abs(wtDiff) < 0.005 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`reconcile-wtdiff-${idx}`}>
                          {wtDiff != null ? `${wtDiff > 0 ? '+' : ''}${wtDiff.toFixed(3)}` : '—'}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                {reconciledCount > 0 && (
                  <span className="text-sm text-emerald-700" data-testid="reconciled-count-note">
                    {reconciledCount} of {rows.length} already reconciled{pendingCount === 0 ? ' — nothing to submit' : ` · ${pendingCount} pending`}
                  </span>
                )}
                <Button variant="outline" onClick={() => exportReconcileCsv(docData, rows)} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="reconcile-export-csv-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={handleSubmit} disabled={submitting || jobRunning || pendingCount === 0} className="bg-violet-600 hover:bg-violet-700" data-testid="reconcile-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Reconciliation
                </Button>
              </div>
              <p className="mt-2 text-right text-xs text-gray-500" data-testid="reconcile-qty-only-note">Reconcile records each bundle's qty only — USN stickers are bound later at Segregation / Job Worker Return.</p>
            </CardContent>
          </Card>
        )}

        {/* Live job status */}
        {job && (
          <Card className={`border-2 ${job.status === 'failed' ? 'border-red-400' : job.status === 'completed_with_errors' ? 'border-amber-400' : 'border-violet-400'}`} data-testid="reconcile-job-status">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                {(() => { const m = statusMeta(job.status); const Icon = m.icon; return (
                  <>
                    <Icon className={`w-5 h-5 ${jobRunning ? 'animate-spin' : ''}`} />
                    <span>Reconciliation</span>
                    <Badge className={`${statusMeta(job.status).cls} ml-2`}>{statusMeta(job.status).label}</Badge>
                  </>
                ); })()}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className={`h-3 rounded-full transition-all ${job.status === 'failed' ? 'bg-red-500' : 'bg-violet-600'}`} style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#5A5F6E]">
                <span>Bundles: <b className="text-[#1A1F2E]">{job.processed_bundles || 0}/{job.total_bundles || 0}</b></span>
                <span>Qty reconciled: <b className="text-[#1A1F2E]" data-testid="reconcile-job-qty">{job.reconciled_qty || job.assigned_usns || 0}</b></span>
                <span>Target qty: <b className="text-[#1A1F2E]">{job.total_qty || 0}</b></span>
              </div>
              {job.error && <p className="text-sm text-red-600">Error: {job.error}</p>}
              {job.errors && job.errors.length > 0 && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm max-h-40 overflow-y-auto">
                  <p className="font-semibold text-amber-800 mb-1">{job.errors.length} issue(s):</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                    {job.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reconcile report */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg"><Package className="w-5 h-5 text-violet-600" /> Reconcile Report</CardTitle>
              <Button variant="ghost" size="sm" onClick={loadReport} data-testid="reconcile-report-refresh"><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {report.length === 0 ? (
              <p className="text-center text-gray-500 py-6">No reconciliations yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="reconcile-report-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Bundles</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.map((j) => {
                      const m = statusMeta(j.status);
                      return (
                        <TableRow key={j.job_id} data-testid={`report-row-${j.job_id}`}>
                          <TableCell className="font-mono text-sm">{j.document_barcode}</TableCell>
                          <TableCell><Badge className={m.cls}>{m.label}</Badge>{j.errors?.length ? <span className="text-xs text-amber-600 ml-1">({j.errors.length} err)</span> : null}</TableCell>
                          <TableCell className="text-right text-sm">{j.processed_bundles || 0}/{j.total_bundles || 0}</TableCell>
                          <TableCell className="text-right text-sm">{j.reconciled_qty || j.assigned_usns || 0}</TableCell>
                          <TableCell className="text-sm">{j.staff_name || j.staff_id}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Reconcile;
